import {
    PhoneArrowUpRightIcon,
    PhoneIcon,
    PhoneXMarkIcon,
} from "@heroicons/react/24/outline";
import {
    ActivityParentEntityId,
    isPhoneNumberE164,
    PhoneNumberE164,
    TaskTypeId,
    TelecomPhoneLineId,
    toPhoneNumberE164,
    UserId,
    VoiceCallId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { Call, Device } from "@twilio/voice-sdk";
import { trpc } from "components/rpc/trpc";
import { DangerButton } from "components/ui/danger-button";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import {
    GetPersonalPhoneNumbersQuery,
    useLogVoiceCallMutation,
    useRequestVoiceTokenMutation,
} from "generated/graphql";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { useCurrentUserId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { inAdvancedMode } from "util/browser";
import { handleEvent } from "util/user-events";
import { CallFromChoiceOptions, useCallFrom } from "./call-from-options";
import {
    forgetBridgeCall,
    PlacedBridgeCall,
    rememberBridgeCall,
    rememberedBridgeCallFor,
} from "./remembered-bridge-call";

interface PhoneCallProps {
    phoneNumber: string;
    entityId?: ActivityParentEntityId;
    activityTypeId: TaskTypeId | null;
}

type PhoneLine = GetPersonalPhoneNumbersQuery["getPersonalPhoneNumbers"][number];

type BridgeCallStatus =
    inferRouterOutputs<AppRouter>["telecom"]["getBridgeCallStatus"]["status"];

interface BridgeCallLog {
    entityId: ActivityParentEntityId;
    activityTypeId: TaskTypeId | null;
}

type DisplayedCallStatus =
    | "ready"
    | "calling"
    | "callingYourPhone"
    | "answerYourPhone"
    | "connected"
    | "ended"
    | "none";

type CallState =
    | { type: "idle" }
    | { type: "calling" }
    | { type: "connected" }
    | { type: "callingYourPhone" }
    | { type: "onYourPhone"; callSid: string; hangUpError: string | null }
    | { type: "ended" }
    | { type: "error"; message: string };

type CallAction =
    | { type: "startCall" }
    | { type: "callConnected" }
    | { type: "startPhoneCall" }
    | { type: "phoneCallPlaced"; callSid: string }
    | { type: "hangUpFailed"; message: string }
    | { type: "endCall" }
    | { type: "callEnded" }
    | { type: "error"; message: string }
    | { type: "reset" };

const callReducer = (state: CallState, action: CallAction): CallState => {
    switch (action.type) {
        case "startCall":
            return { type: "calling" };
        case "callConnected":
            return { type: "connected" };
        case "startPhoneCall":
            return { type: "callingYourPhone" };
        case "phoneCallPlaced":
            return {
                type: "onYourPhone",
                callSid: action.callSid,
                hangUpError: null,
            };
        case "hangUpFailed":
            return state.type === "onYourPhone"
                ? { ...state, hangUpError: action.message }
                : state;
        case "endCall":
            return { type: "ended" };
        case "callEnded":
            return { type: "ended" };
        case "error":
            return { type: "error", message: action.message };
        case "reset":
            return { type: "idle" };
    }
    const exhaustiveCheck: never = action;
    throw new Error(`Unhandled call action: ${JSON.stringify(exhaustiveCheck)}`);
};

const isAudioOutputSelectionError = (error: unknown): boolean => {
    if (!(error instanceof Error)) {
        return false;
    }

    if (
        error.name === "NotSupportedError" &&
        error.message.includes("Audio output selection")
    ) {
        return true;
    }

    if (error.name === "NotAllowedError" && error.message.includes("user gesture")) {
        return true;
    }

    return false;
};

const choosesCallFrom = (state: CallState): boolean =>
    state.type === "idle" || state.type === "ended" || state.type === "error";

const hangUpFailureMessage = "We couldn't hang up the call. Try again.";

const bridgeCallPollIntervalMs = 3000;

const useBridgeCallStatusQuery = (call: PlacedBridgeCall | null) => {
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.telecom.getBridgeCallStatus.queryOptions(call ?? skipToken),
        refetchInterval: current =>
            current.state.status === "error" || current.state.data?.status === "ended"
                ? false
                : bridgeCallPollIntervalMs,
    });
    return { status: query.data?.status ?? null, lost: query.isError };
};

const useBridgeCall = (
    phoneLineId: TelecomPhoneLineId,
    userId: UserId,
    callSid: string | null,
) => {
    const trpcClient = trpc.useTRPC();
    const placeBridgeCall = useMutation(
        trpcClient.telecom.placeBridgeCall.mutationOptions(),
    );
    const endBridgeCall = useMutation(trpcClient.telecom.endBridgeCall.mutationOptions());
    const followed = useBridgeCallStatusQuery(callSid ? { phoneLineId, callSid } : null);
    const status = callSid ? (followed.status ?? "callingUser") : null;

    useEffect(() => {
        if (callSid && status === "ended") {
            forgetBridgeCall(callSid);
        }
    }, [callSid, status]);

    const place = async (
        to: PhoneNumberE164,
        log: BridgeCallLog | null,
    ): Promise<string> => {
        const placed = await placeBridgeCall.mutateAsync({ phoneLineId, to, log });
        rememberBridgeCall(userId, { phoneLineId, callSid: placed.callSid });
        return placed.callSid;
    };

    const hangUp = async (placedCallSid: string): Promise<void> => {
        await endBridgeCall.mutateAsync({ phoneLineId, callSid: placedCallSid });
        forgetBridgeCall(placedCallSid);
    };

    return {
        status,
        lost: followed.lost,
        hangingUp: endBridgeCall.isPending,
        place,
        hangUp,
    };
};

const useEarlierCallOnYourPhone = (userId: UserId, state: CallState): boolean => {
    const [earlierCall] = useState(() => rememberedBridgeCallFor(userId));
    const { status, lost } = useBridgeCallStatusQuery(
        earlierCall && choosesCallFrom(state) ? earlierCall : null,
    );

    useEffect(() => {
        if (earlierCall && (status === "ended" || lost)) {
            forgetBridgeCall(earlierCall.callSid);
        }
    }, [earlierCall, status, lost]);

    return !lost && status !== null && status !== "ended";
};

const displayedBridgeCallStatusOf = (
    status: BridgeCallStatus | null,
): DisplayedCallStatus => {
    switch (status) {
        case null:
        case "callingUser":
            return "answerYourPhone";
        case "callingContact":
            return "calling";
        case "connected":
            return "connected";
        case "ended":
            return "ended";
    }
    const exhaustiveCheck: never = status;
    throw new Error(`Unhandled bridge call status: ${JSON.stringify(exhaustiveCheck)}`);
};

const displayedCallStatusOf = (
    state: CallState,
    bridgeCallStatus: BridgeCallStatus | null,
): DisplayedCallStatus => {
    switch (state.type) {
        case "idle":
            return "ready";
        case "calling":
            return "calling";
        case "connected":
            return "connected";
        case "callingYourPhone":
            return "callingYourPhone";
        case "onYourPhone":
            return displayedBridgeCallStatusOf(bridgeCallStatus);
        case "ended":
            return "ended";
        case "error":
            return "none";
    }
    const exhaustiveCheck: never = state;
    throw new Error(`Unhandled call state: ${JSON.stringify(exhaustiveCheck)}`);
};

const ProgressText: React.FC<{ text: string }> = ({ text }) => (
    <div className="flex items-center justify-center space-x-2">
        <Spinner />
        <p className="text-sm text-gray-600">{text}</p>
    </div>
);

const CallStatusText: React.FC<{ status: DisplayedCallStatus }> = ({ status }) => {
    switch (status) {
        case "ready":
            return <p className="text-sm text-gray-600">Ready to call</p>;
        case "calling":
            return <ProgressText text="Calling..." />;
        case "callingYourPhone":
            return <ProgressText text="Calling your phone..." />;
        case "answerYourPhone":
            return (
                <p className="text-sm text-gray-600">
                    Answer your phone and press any key.
                </p>
            );
        case "connected":
            return (
                <div className="flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <p className="text-sm text-green-600 font-medium">Connected</p>
                </div>
            );
        case "ended":
            return <p className="text-sm text-gray-600">Call ended</p>;
        case "none":
            return null;
    }
    const exhaustiveCheck: never = status;
    throw new Error(`Unhandled displayed status: ${JSON.stringify(exhaustiveCheck)}`);
};

export const PhoneCall: React.FC<PhoneCallProps> = props => {
    const phoneLines = usePhoneLines();

    if (!isPhoneNumberE164(props.phoneNumber) || phoneLines.length === 0) {
        return null;
    }

    return (
        <PhoneCallOnLine
            {...props}
            to={toPhoneNumberE164(props.phoneNumber)}
            phoneLine={phoneLines[0]}
        />
    );
};

interface PhoneCallOnLineProps extends PhoneCallProps {
    to: PhoneNumberE164;
    phoneLine: PhoneLine;
}

const PhoneCallOnLine: React.FC<PhoneCallOnLineProps> = props => {
    const { phoneNumber, to, entityId, activityTypeId, phoneLine } = props;
    const userId = useCurrentUserId();
    const [state, dispatch] = useReducer(callReducer, { type: "idle" });
    const deviceRef = useRef<Device | null>(null);
    const callRef = useRef<Call | null>(null);
    const isCallingRef = useRef(false);
    const requestVoiceToken = useRequestVoiceTokenMutation();
    const logVoiceCall = useLogVoiceCallMutation();
    const callFromChoice = useCallFrom(phoneLine.id);
    const bridgeCall = useBridgeCall(
        phoneLine.id,
        userId,
        state.type === "onYourPhone" ? state.callSid : null,
    );
    const displayedStatus = displayedCallStatusOf(state, bridgeCall.status);
    const onAnotherCallFromYourPhone = useEarlierCallOnYourPhone(userId, state);

    useEffect(() => {
        if (bridgeCall.status === "ended") {
            isCallingRef.current = false;
            dispatch({ type: "callEnded" });
        }
    }, [bridgeCall.status]);

    // Cleanup device on unmount
    useEffect(() => {
        return () => {
            cleanupDevice();
        };
    }, []);

    const cleanupDevice = () => {
        if (callRef.current) {
            callRef.current.disconnect();
            callRef.current = null;
        }
        if (deviceRef.current) {
            deviceRef.current.destroy();
            deviceRef.current = null;
        }
        isCallingRef.current = false;
    };

    const handleError = (error: unknown, context: string) => {
        if (isAudioOutputSelectionError(error)) {
            return;
        }
        cleanupDevice();
        const errorMessage =
            error instanceof Error
                ? error.message
                : String(error) || `Failed to ${context.toLowerCase()}`;
        dispatch({ type: "error", message: errorMessage });
    };

    const logCall = async (): Promise<VoiceCallId | null> => {
        if (!entityId) {
            return null;
        }
        try {
            const result = await logVoiceCall.mutateAsync({
                entityId,
                phoneNumber: to,
                phoneLineId: phoneLine.id,
                activityTypeId,
            });
            return result.logVoiceCall;
        } catch (error) {
            console.error("Failed to log voice call:", error);
            return null;
        }
    };

    const placePhoneCall = async () => {
        const callSid = await bridgeCall.place(
            to,
            entityId ? { entityId, activityTypeId } : null,
        );
        dispatch({ type: "phoneCallPlaced", callSid });
    };

    const startBrowserCall = async (voiceCallId: VoiceCallId | null) => {
        cleanupDevice();

        const token = await requestVoiceToken.mutateAsync({
            phoneLineId: phoneLine.id,
        });

        const device = new Device(token.requestVoiceToken.token, {
            logLevel: inAdvancedMode() ? "debug" : "silent",
        });

        deviceRef.current = device;

        device.on("error", error => {
            handleError(error, "Device error");
        });

        const dialParams: Record<string, string> = {
            To: to,
            From: phoneLine.phoneNumber,
        };
        if (voiceCallId) {
            dialParams.VoiceCallId = voiceCallId;
        }

        const call = await device.connect({
            params: dialParams,
        });

        callRef.current = call;

        call.on("accept", async () => {
            dispatch({ type: "callConnected" });
        });

        call.on("reject", () => {
            isCallingRef.current = false;
            dispatch({ type: "callEnded" });
        });

        call.on("disconnect", () => {
            isCallingRef.current = false;
            dispatch({ type: "callEnded" });
        });

        call.on("error", error => {
            handleError(error, "Call error");
        });

        device.on("disconnect", () => {
            isCallingRef.current = false;
            dispatch({ type: "callEnded" });
        });
    };

    const handleStartCall = async () => {
        if (isCallingRef.current) {
            return;
        }
        const { callFrom } = callFromChoice;

        try {
            isCallingRef.current = true;
            dispatch({ type: callFrom === "phone" ? "startPhoneCall" : "startCall" });

            handleEvent("initiate-voice-call", {
                phoneLineId: phoneLine.id,
            });

            if (callFrom === "phone") {
                await placePhoneCall();
            } else {
                await startBrowserCall(await logCall());
            }
        } catch (error) {
            handleError(error, "Failed to start call");
        }
    };

    const handleEndCall = () => {
        cleanupDevice();
        dispatch({ type: "endCall" });
    };

    const handleHangUpPhoneCall = async () => {
        if (state.type !== "onYourPhone") {
            return;
        }
        try {
            await bridgeCall.hangUp(state.callSid);
            isCallingRef.current = false;
            dispatch({ type: "endCall" });
        } catch {
            dispatch({ type: "hangUpFailed", message: hangUpFailureMessage });
        }
    };

    const handleCallAgain = async () => {
        handleStartCall();
    };

    return (
        <div className="flex flex-col items-center space-y-6 p-6 bg-white rounded-lg shadow-lg max-w-sm mx-auto">
            {/* Contact Info */}
            <div className="text-center">
                <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PhoneIcon className="w-8 h-8 text-brand-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{phoneNumber}</h3>
            </div>

            <div className="text-center">
                <CallStatusText status={displayedStatus} />
            </div>

            {bridgeCall.lost ? (
                <InlineBanner type="warning" showIcon={true}>
                    We couldn&apos;t check on the call.
                </InlineBanner>
            ) : null}

            {onAnotherCallFromYourPhone ? (
                <InlineBanner type="warning" showIcon={true}>
                    You&apos;re already on a call using your phone.
                </InlineBanner>
            ) : null}

            {choosesCallFrom(state) ? (
                <div className="w-full">
                    <CallFromChoiceOptions label="Call using" choice={callFromChoice} />
                </div>
            ) : null}

            <div className="flex flex-col space-y-3 w-full">
                {state.type === "idle" ? (
                    <PrimaryButton
                        onClick={handleStartCall}
                        disabled={isCallingRef.current}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneArrowUpRightIcon className="w-5 h-5" />
                        <span>Call</span>
                    </PrimaryButton>
                ) : null}

                {state.type === "calling" ? (
                    <DangerButton
                        onClick={handleEndCall}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneXMarkIcon className="w-5 h-5" />
                        <span>Cancel</span>
                    </DangerButton>
                ) : null}

                {state.type === "connected" ? (
                    <DangerButton
                        onClick={handleEndCall}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneXMarkIcon className="w-5 h-5" />
                        <span>Hang Up</span>
                    </DangerButton>
                ) : null}

                {state.type === "onYourPhone" ? (
                    <DangerButton
                        onClick={handleHangUpPhoneCall}
                        disabled={bridgeCall.hangingUp}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneXMarkIcon className="w-5 h-5" />
                        <span>
                            {displayedStatus === "connected" ? "Hang Up" : "Cancel"}
                        </span>
                    </DangerButton>
                ) : null}

                {state.type === "ended" ? (
                    <PrimaryButton
                        onClick={handleCallAgain}
                        disabled={isCallingRef.current || bridgeCall.hangingUp}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneArrowUpRightIcon className="w-5 h-5" />
                        <span>Call Again</span>
                    </PrimaryButton>
                ) : null}

                {state.type === "error" ? (
                    <PrimaryButton
                        onClick={handleCallAgain}
                        disabled={isCallingRef.current}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneArrowUpRightIcon className="w-5 h-5" />
                        <span>Try Again</span>
                    </PrimaryButton>
                ) : null}
            </div>

            {displayedStatus === "connected" ? (
                <div className="text-center">
                    <p className="text-xs text-gray-500">Call in progress</p>
                </div>
            ) : null}

            {state.type === "onYourPhone" && state.hangUpError ? (
                <InlineBanner type="error" showIcon={true}>
                    {state.hangUpError}
                </InlineBanner>
            ) : null}

            {state.type === "error" ? (
                <InlineBanner type="error" showIcon={true}>
                    {state.message}
                </InlineBanner>
            ) : null}
        </div>
    );
};
